import {
  BookOpen,
  Building2,
  FileText,
  GitBranch,
  type LucideIcon,
  Map as MapIcon,
  Settings,
  User as UserIcon,
} from 'lucide-react';

/**
 * Codes de phase reconnus côté UI.
 * Ajouter ici toute nouvelle phase (ex : 'phase-9') si on en introduit une.
 */
export type PhaseCode =
  | 'phase-0'
  | 'phase-1'
  | 'phase-2'
  | 'phase-3'
  | 'phase-4'
  | 'phase-5'
  | 'phase-6'
  | 'phase-7'
  | 'phase-8';

/**
 * Ordre numérique d'une phase. Plus le chiffre est élevé, plus la phase est avancée.
 * Une phase inconnue retombe sur -1 (jamais visible).
 */
export const PHASE_ORDER: Record<PhaseCode, number> = {
  'phase-0': 0,
  'phase-1': 1,
  'phase-2': 2,
  'phase-3': 3,
  'phase-4': 4,
  'phase-5': 5,
  'phase-6': 6,
  'phase-7': 7,
  'phase-8': 8,
};

export interface MenuItem {
  /** Identifiant stable, sert de `key` React. */
  id: string;
  /** Libellé affiché dans la sidebar. */
  label: string;
  /** Route TanStack. `undefined` = parent purement toggle (clic ouvre/ferme). */
  to?: string;
  /** Icône Lucide affichée à gauche du libellé. Optionnelle pour les sous-items. */
  icon?: LucideIcon;
  /** Phase à partir de laquelle l'item devient visible (visibilité accumulée). */
  appearsIn: PhaseCode;
  /**
   * Phases pour lesquelles l'item porte une étoile.
   * Pour un parent (ayant `children`), l'étoile est aussi affichée si l'un de ses enfants
   * porte une étoile dans la phase sélectionnée.
   */
  starredIn: PhaseCode[];
  /** Emplacement dans la sidebar. `bottom` = sous le séparateur (zone Compte). */
  position?: 'top' | 'bottom';
  /** Sous-items affichés dans un dropdown. */
  children?: MenuItem[];
  /**
   * Clé pour afficher une pastille de compteur (ex. nombre de documents à valider).
   * Les totaux sont fournis par le `Layout` via `NavItem`.
   */
  badgeKey?: string;
}

/**
 * Source unique de vérité de la navigation.
 *
 * Règles :
 *   - Visibilité d'un item = phase **utilisateur sur le site actif** (`userPhaseCode` dans
 *     `MyWorkSiteDto`) : `phaseOrderOf(userPhaseCode) >= PHASE_ORDER[item.appearsIn]`
 *   - Étoile d'un item    = `selectedPhase ∈ item.starredIn`
 *   - Étoile d'un parent  = OR des étoiles de ses enfants visibles
 *
 * Pour ajouter / déplacer un item de menu, modifier UNIQUEMENT ce tableau.
 * Voir `apps/web/src/components/MENU.md` pour le guide complet.
 */
export const MENU_CONFIG: MenuItem[] = [
  {
    id: 'parcours',
    label: 'Parcours',
    to: '/',
    icon: MapIcon,
    appearsIn: 'phase-0',
    starredIn: ['phase-0'],
    position: 'top',
  },

  {
    id: 'cartographie',
    label: 'Cartographie',
    to: '/cartographie',
    icon: GitBranch,
    appearsIn: 'phase-3',
    starredIn: ['phase-3'],
    position: 'top',
  },

  {
    id: 'company',
    label: 'Mon entreprise',
    icon: Building2,
    appearsIn: 'phase-0',
    starredIn: [],
    position: 'top',
    children: [
      {
        id: 'company-profile',
        label: 'Profil entreprise',
        to: '/admin/company',
        appearsIn: 'phase-0',
        starredIn: ['phase-0', 'phase-1'],
      },
      {
        id: 'company-sites',
        label: 'Gérer les sites',
        to: '/admin/sites',
        appearsIn: 'phase-0',
        starredIn: ['phase-0'],
      },
      {
        id: 'company-modules',
        label: 'Mes modules',
        to: '/modules',
        appearsIn: 'phase-1',
        starredIn: ['phase-1'],
      },
      {
        id: 'company-users',
        label: 'Collaborateurs',
        to: '/admin/users',
        appearsIn: 'phase-1',
        starredIn: ['phase-1'],
      },
    ],
  },

  {
    id: 'publication',
    label: 'Publication',
    icon: BookOpen,
    appearsIn: 'phase-2',
    starredIn: ['phase-2'],
    position: 'top',
    children: [
      {
        id: 'publication-pending',
        label: 'Documents à valider',
        to: '/publication/a-valider',
        appearsIn: 'phase-2',
        starredIn: ['phase-2'],
        badgeKey: 'publication-pending',
      },
      {
        id: 'publication-rejected',
        label: 'Documents refusés',
        to: '/publication/refus',
        appearsIn: 'phase-2',
        starredIn: ['phase-2'],
        badgeKey: 'publication-rejected',
      },
      {
        id: 'publication-published',
        label: 'Documents publiés',
        to: '/publication/publie',
        appearsIn: 'phase-2',
        starredIn: ['phase-2'],
      },
    ],
  },

  {
    id: 'documents',
    label: 'Documents',
    to: '/documents',
    icon: FileText,
    appearsIn: 'phase-2',
    starredIn: ['phase-2'],
    position: 'top',
    children: [
      {
        id: 'documents-swot',
        label: 'Analyse SWOT',
        to: '/contexte/swot',
        appearsIn: 'phase-2',
        starredIn: ['phase-2'],
      },
      {
        id: 'documents-stakeholders',
        label: 'Parties intéressées',
        to: '/contexte/parties-prenantes',
        appearsIn: 'phase-2',
        starredIn: ['phase-2'],
      },
    ],
  },

  {
    id: 'parametres',
    label: 'Paramètres',
    icon: Settings,
    appearsIn: 'phase-3',
    starredIn: ['phase-3'],
    position: 'top',
    children: [
      {
        id: 'parametres-groupes-processus',
        label: 'Groupes et processus',
        to: '/parametres/groupes-et-processus',
        appearsIn: 'phase-3',
        starredIn: ['phase-3'],
      },
      {
        id: 'parametres-structure-processus',
        label: 'Structure des processus',
        to: '/parametres/structure-des-processus',
        appearsIn: 'phase-3',
        starredIn: ['phase-3'],
      },
    ],
  },

  {
    id: 'account',
    label: 'Compte',
    to: '/account',
    icon: UserIcon,
    appearsIn: 'phase-0',
    starredIn: ['phase-0'],
    position: 'bottom',
  },
];

/** Retourne l'ordre d'une phase. -1 si inconnue. */
export function phaseOrderOf(code: string | null | undefined): number {
  if (!code) return -1;
  return PHASE_ORDER[code as PhaseCode] ?? -1;
}

/**
 * Indique si un item (ou l'un de ses descendants) doit porter une étoile pour la
 * phase sélectionnée. Pour les parents, on délègue aux enfants visibles.
 */
export function isItemStarred(
  item: MenuItem,
  selectedPhase: PhaseCode | null,
  userPhaseOrder: number,
): boolean {
  if (!selectedPhase) return false;
  if (item.children && item.children.length > 0) {
    return item.children.some(
      (child) =>
        phaseOrderOf(child.appearsIn) <= userPhaseOrder &&
        isItemStarred(child, selectedPhase, userPhaseOrder),
    );
  }
  return item.starredIn.includes(selectedPhase);
}

/** Indique si un item est visible pour la phase utilisateur sur le site actif. */
export function isItemVisible(item: MenuItem, userPhaseOrder: number): boolean {
  return phaseOrderOf(item.appearsIn) <= userPhaseOrder;
}
